import { WebRtcTestHandler } from '../WebRtcTestHandler';
import { type Test, testStates } from './Test';

export class ConnectionTest implements Test {
  private readonly request = 'hello';
  private readonly response = 'world';
  private readonly timeoutThreshold = 50 * 1000;

  private rtcPeerConn1: RTCPeerConnection | undefined;
  private rtcPeerConn2: RTCPeerConnection | undefined;

  private datachannel1: RTCDataChannel | undefined;
  private datachannel2: RTCDataChannel | undefined;
  private timeout: number | undefined;

  private foundCandidate1: boolean;
  private foundCandidate2: boolean;

  private readonly errorMessages = {
    RECEIVED_WRONG_REQUEST: 'Es wurde eine falsche Anfrage gesendet.',
    RECEIVED_WRONG_RESPONSE: 'Es wurde eine falsche Antwort gesendet.',
    COULD_NOT_SEND_REQUEST: 'Es konnte keine Anfrage gesendet werden.',
    COULD_NOT_SEND_RESPONSE: 'Es konnte keine Antwort gesendet werden.'
  };

  public testState = testStates['not started'];
  public readonly name = 'ConnectionTest';

  constructor (
    private readonly rtcConfiguration: RTCConfiguration,
    public readonly testMethod: (candidate: RTCIceCandidate) => boolean,
    private readonly handler: WebRtcTestHandler,
    public readonly resultElement: HTMLLIElement,
    public readonly isCritical: boolean) {
    this.foundCandidate1 = false;
    this.foundCandidate2 = false;
  }

  public async startTest () {
    this.testState = testStates.running;
    try {
      this.rtcPeerConn1 = new RTCPeerConnection(this.rtcConfiguration);
      this.rtcPeerConn2 = new RTCPeerConnection(this.rtcConfiguration);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_PEER_CONNECTION);
      return;
    }

    this.rtcPeerConn1.onicecandidate = (evt) => {
      const iceCandidate = evt.candidate;
      if (!iceCandidate) {
        if (!this.foundCandidate1) {
          this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_FIND_MATCHING_CANDIDATE);
        }
        return;
      }
      if (this.testMethod(iceCandidate)) {
        this.rtcPeerConn2?.addIceCandidate(iceCandidate);
        this.foundCandidate1 = true;
      }
    };

    this.rtcPeerConn2.onicecandidate = (evt) => {
      const iceCandidate = evt.candidate;
      if (!iceCandidate) {
        if (!this.foundCandidate2) {
          this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_FIND_MATCHING_CANDIDATE);
        }
        return;
      }
      if (this.testMethod(iceCandidate)) {
        this.rtcPeerConn1?.addIceCandidate(iceCandidate);
        this.foundCandidate2 = true;
      }
    };

    this.rtcPeerConn2.ondatachannel = (evt: RTCDataChannelEvent) => {
      this.datachannel2 = evt.channel;
      this.datachannel2.onmessage = (evt) => {
        if (evt.data !== this.response) {
          this.endTest(false, this.errorMessages.RECEIVED_WRONG_RESPONSE);
        } else {
          this.endTest(true);
        }
      };

      this.datachannel2.onopen = () => {
        try {
          this.datachannel2!.send(this.request);
        } catch (err) {
          this.endTest(false, this.errorMessages.COULD_NOT_SEND_REQUEST);
        }
      };
    };

    try {
      this.datachannel1 = this.rtcPeerConn1.createDataChannel('test1');

      this.datachannel1.onmessage = (evt) => {
        if (evt.data !== this.request) {
          this.endTest(false, this.errorMessages.RECEIVED_WRONG_REQUEST);
        } else {
          try {
            this.datachannel1?.send(this.response);
          } catch (err) {
            this.endTest(false, this.errorMessages.COULD_NOT_SEND_RESPONSE);
          }
        }
      };
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_DATA_CHANNEL);
    }
    await this.establishConnection();

    this.timeout = setTimeout(this.endTest.bind(this), this.timeoutThreshold, false, WebRtcTestHandler.errorMessages.TIMEOUT);
  }

  public endTest (successful: boolean, message?: string) {
    successful ? this.testState = testStates.succeded : this.testState = testStates.failed;
    message ? this.handler.displayResult(successful, this, message) : this.handler.displayResult(successful, this);

    this.rtcPeerConn1?.close();
    this.rtcPeerConn2?.close();
    this.datachannel1?.close();
    this.datachannel2?.close();

    clearTimeout(this.timeout);
  }

  private async establishConnection () {
    if (!(this.rtcPeerConn1 && this.rtcPeerConn2)) {
      this.endTest(false, WebRtcTestHandler.errorMessages.PEER_CONNECTION_NOT_FOUND);
      return;
    }

    try {
      const offer = await this.rtcPeerConn1.createOffer();
      await this.rtcPeerConn1.setLocalDescription(offer);
      await this.rtcPeerConn2.setRemoteDescription(offer);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_OFFER);
      return;
    }

    try {
      var answer = await this.rtcPeerConn2.createAnswer();
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_ANSWER);
      return;
    }

    try {
      await this.rtcPeerConn2.setLocalDescription(answer);
      await this.rtcPeerConn1.setRemoteDescription(answer);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_SET_ANSWER_DESCRIPTION);
    }
  }
}
